// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { SNF_PHASES, admitBridge, bridgeEndpoint, bridgeId, defineBridgeWitness, defineSeam, isFeedbackFreeTrace, isNeutralizationTheoremCode, isSeamNormalFormTrace, witnessKindForTheorem, } from "@csk/core";
import { WASM_THEOREM_META } from "./meta.js";
import { createStatefulSeamBridgeContract } from "./seam-bridge-state.js";
import { serializeSquareDecision, squareIsForbidden } from "./squares.js";
function theoremMeta(code) {
    const meta = WASM_THEOREM_META[code];
    if (meta === undefined) {
        throw new Error(`Unknown theorem code: ${code}`);
    }
    return meta;
}
export function seamDescriptorForTheorem(code) {
    const meta = theoremMeta(code);
    return {
        theoremCode: code,
        theoremName: meta.name,
        seamOrientation: meta.seamType,
        forbiddenGenerator: meta.generator,
        requiredWitnessKind: witnessKindForTheorem(code),
    };
}
export function parseWasmSeamDescriptor(serialized) {
    let value;
    try {
        value = JSON.parse(serialized);
    }
    catch {
        return null;
    }
    if (typeof value !== "object" || value === null)
        return null;
    const record = value;
    const theoremCode = record["theoremCode"];
    if (!isNeutralizationTheoremCode(theoremCode))
        return null;
    const expected = seamDescriptorForTheorem(theoremCode);
    if (record["theoremName"] !== expected.theoremName ||
        record["seamOrientation"] !== expected.seamOrientation ||
        record["forbiddenGenerator"] !== expected.forbiddenGenerator ||
        record["requiredWitnessKind"] !== expected.requiredWitnessKind) {
        return null;
    }
    return expected;
}
export function parseSeamNormalFormTraceJson(phasesJson) {
    const value = JSON.parse(phasesJson);
    if (Array.isArray(value)) {
        return { phases: value };
    }
    if (typeof value === "object" && value !== null) {
        const phases = value["phases"];
        if (Array.isArray(phases)) {
            return { phases };
        }
    }
    throw new Error("SNF trace must be a JSON array or an object with phases");
}
function requireTheoremCode(value) {
    if (isNeutralizationTheoremCode(value)) {
        return value;
    }
    throw new Error(`Unknown theorem code: ${value}`);
}
function optionalWitnessTheoremCode(value) {
    const trimmed = value.trim();
    if (trimmed.length === 0)
        return null;
    return isNeutralizationTheoremCode(trimmed) ? trimmed : "invalid";
}
function bridgeForAttempt(options) {
    const meta = theoremMeta(options.theoremCode);
    const scope = `wasm-seam:${options.theoremCode}`;
    const witness = options.witnessTheoremCode === null
        ? null
        : defineBridgeWitness({
            id: `wasm-witness:${options.theoremCode}:${options.witnessTheoremCode}`,
            theorem: options.witnessTheoremCode,
            issuer: "wasm-seam-bridge",
            scope,
            issuedAt: 1,
        });
    return {
        id: bridgeId(`wasm-bridge:${options.theoremCode}`),
        from: bridgeEndpoint("source", "typescript-host", {
            lane: "Zone1",
            domain: "typescript",
        }),
        to: bridgeEndpoint("target", "wasm-kernel", {
            lane: "Zone2",
            domain: "wasm",
        }),
        seam: defineSeam({
            id: `wasm-seam:${options.theoremCode}`,
            orientation: meta.seamType,
            theorem: options.theoremCode,
            forbiddenPattern: `${meta.generator}:${meta.name}`,
        }),
        witness,
        phaseCoverage: SNF_PHASES,
        lifecycle: "proposed",
        consumption: options.consumption ?? null,
        receipt: null,
    };
}
export function admitSeamBridgeAttempt(options) {
    const bridge = bridgeForAttempt(options);
    const context = {
        scope: `wasm-seam:${options.theoremCode}`,
        forbiddenSquare: options.forbiddenSquare,
        now: 2,
        receipt: {
            consumedResources: [`seam:${options.theoremCode}`],
            trace: options.trace,
        },
    };
    const result = admitBridge(bridge, options.consumption === undefined || options.consumption === null
        ? context
        : { ...context, consumption: options.consumption });
    return {
        status: result.verdict.status,
        consumption: result.consumption,
    };
}
export function createSeamBridgeKernelContract() {
    const statefulContract = createStatefulSeamBridgeContract();
    return {
        seam_descriptor(theorem_code) {
            return JSON.stringify(seamDescriptorForTheorem(requireTheoremCode(theorem_code)));
        },
        seam_validate_snf_trace(phases_json) {
            try {
                const trace = parseSeamNormalFormTraceJson(phases_json);
                return isFeedbackFreeTrace(trace) && isSeamNormalFormTrace(trace);
            }
            catch {
                return false;
            }
        },
        ...statefulContract,
        bridge_admit_status(theorem_code, witness_theorem_code, phases_json, forbidden_square) {
            const theoremCode = requireTheoremCode(theorem_code);
            const witnessTheoremCode = optionalWitnessTheoremCode(witness_theorem_code);
            if (witnessTheoremCode === "invalid") {
                return "rejected-invalid-lifecycle";
            }
            let trace;
            try {
                trace = parseSeamNormalFormTraceJson(phases_json);
            }
            catch {
                return "rejected-invalid-lifecycle";
            }
            return admitSeamBridgeAttempt({
                theoremCode,
                witnessTheoremCode,
                trace,
                forbiddenSquare: forbidden_square,
            }).status;
        },
        bridge_admit_square_status(theorem_code, witness_theorem_code, phases_json, generator_code) {
            const theoremCode = requireTheoremCode(theorem_code);
            const witnessTheoremCode = optionalWitnessTheoremCode(witness_theorem_code);
            if (witnessTheoremCode === "invalid") {
                return "rejected-invalid-lifecycle";
            }
            let trace;
            try {
                trace = parseSeamNormalFormTraceJson(phases_json);
            }
            catch {
                return "rejected-invalid-lifecycle";
            }
            return admitSeamBridgeAttempt({
                theoremCode,
                witnessTheoremCode,
                trace,
                forbiddenSquare: squareIsForbidden(theorem_code, generator_code),
            }).status;
        },
        square_decide(theorem_code, generator_code) {
            return serializeSquareDecision(theorem_code, generator_code);
        },
        square_is_forbidden(theorem_code, generator_code) {
            return squareIsForbidden(theorem_code, generator_code);
        },
    };
}
//# sourceMappingURL=seam-bridge-kernel.js.map